import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailAPI } from '../../services/api';
import '../../App.css';

export default function ComposeEmail() {
  const navigate = useNavigate();

  const [recipientType, setRecipientType] = useState('all'); // 'all' | 'user' | 'custom'
  const [users, setUsers]                 = useState([]);
  const [usersLoading, setUsersLoading]   = useState(false);
  const [selectedUser, setSelectedUser]   = useState(null);
  const [userSearch, setUserSearch]       = useState('');
  const [customEmail, setCustomEmail]     = useState('');
  const [subject, setSubject]             = useState('');
  const [body, setBody]                   = useState('');

  const [sending, setSending]             = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Load user list when "Specific User" is selected
  useEffect(() => {
    if (recipientType === 'user' && users.length === 0 && !usersLoading) {
      setUsersLoading(true);
      emailAPI.getUsers()
        .then(res => { if (res.success) setUsers(res.data.users || []); })
        .catch(() => setError('Failed to load user list.'))
        .finally(() => setUsersLoading(false));
    }
  }, [recipientType, users.length, usersLoading]);

  const filteredUsers = useCallback(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.email       || '').toLowerCase().includes(q) ||
      (u.username    || '').toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate
    if (recipientType === 'user' && !selectedUser) {
      setError('Please select a user from the list.'); return;
    }
    if (recipientType === 'custom' && (!customEmail.trim() || !customEmail.includes('@'))) {
      setError('Please enter a valid email address.'); return;
    }
    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!body.trim())    { setError('Message body is required.'); return; }

    const to =
      recipientType === 'all'    ? 'all' :
      recipientType === 'user'   ? selectedUser.userId :
      customEmail.trim();

    const recipientLabel =
      recipientType === 'all'  ? 'all approved users' :
      recipientType === 'user' ? `${selectedUser.displayName || selectedUser.username} (${selectedUser.email})` :
      customEmail.trim();

    if (!window.confirm(`Send "${subject}" to ${recipientLabel}?`)) return;

    setSending(true);
    try {
      const res = await emailAPI.send({ to, subject, body });
      if (res.success) {
        setSuccess(`✓ Email sent to ${res.data.sent} recipient(s).`);
        setSubject('');
        setBody('');
        setSelectedUser(null);
        setCustomEmail('');
      } else {
        setError(res.error || 'Failed to send email.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>✉️ Compose Email</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/admin/notifications')}>
            🔔 Notifications
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
            ← Admin
          </button>
        </div>
      </div>

      <div className="compose-card">
        <form onSubmit={handleSend}>

          {/* Recipient type */}
          <div className="form-group">
            <label>Recipient</label>
            <div className="segment-row">
              {[
                { key: 'all',    label: '📢 All Approved Users' },
                { key: 'user',   label: '👤 Specific User'      },
                { key: 'custom', label: '✉️ Custom Email'       },
              ].map(rt => (
                <button
                  key={rt.key}
                  type="button"
                  className={`segment-btn ${recipientType === rt.key ? 'active' : ''}`}
                  onClick={() => { setRecipientType(rt.key); setSelectedUser(null); setCustomEmail(''); setError(''); }}
                >
                  {rt.label}
                </button>
              ))}
            </div>
          </div>

          {/* All-users notice */}
          {recipientType === 'all' && (
            <div className="info-box">
              This email will be sent to every approved user in the system.
            </div>
          )}

          {/* User picker */}
          {recipientType === 'user' && (
            <div className="form-group">
              <label>Search Users</label>
              <input
                className="form-control"
                placeholder="Type name or email to filter…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              {usersLoading && <p style={{fontSize:'0.85rem',color:'#888'}}>Loading users…</p>}
              {!usersLoading && (
                <div className="user-picker-list">
                  {filteredUsers().length === 0
                    ? <p className="picker-empty">No users found.</p>
                    : filteredUsers().map(u => (
                        <div
                          key={u.userId || u.email}
                          className={`picker-item ${selectedUser?.userId === u.userId ? 'selected' : ''}`}
                          onClick={() => setSelectedUser(u)}
                        >
                          <span className="picker-name">{u.displayName || u.username || 'Unknown'}</span>
                          <span className="picker-email">{u.email}</span>
                        </div>
                      ))
                  }
                </div>
              )}
              {selectedUser && (
                <div className="selected-user">
                  ✓ Selected: <strong>{selectedUser.displayName || selectedUser.username}</strong> — {selectedUser.email}
                  <button type="button" className="btn-icon" onClick={() => setSelectedUser(null)}>✕</button>
                </div>
              )}
            </div>
          )}

          {/* Custom email */}
          {recipientType === 'custom' && (
            <div className="form-group">
              <label>Email Address</label>
              <input
                className="form-control"
                type="email"
                placeholder="recipient@example.com"
                value={customEmail}
                onChange={e => setCustomEmail(e.target.value)}
              />
            </div>
          )}

          {/* Subject */}
          <div className="form-group">
            <label>Subject *</label>
            <input
              className="form-control"
              placeholder="Email subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              required
            />
          </div>

          {/* Body */}
          <div className="form-group">
            <label>Message *</label>
            <textarea
              className="form-control"
              rows={12}
              placeholder="Write your message here…"
              value={body}
              onChange={e => setBody(e.target.value)}
              required
            />
          </div>

          {error   && <p className="error-msg">{error}</p>}
          {success && <p className="success-msg">{success}</p>}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-send" disabled={sending}>
              {sending ? 'Sending…' : '📤 Send Email'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setSubject(''); setBody(''); setSelectedUser(null); setCustomEmail(''); setError(''); setSuccess(''); }}>
              Clear
            </button>
          </div>
        </form>
      </div>

      <style jsx="true">{`
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .page-header h1 { margin: 0; }
        .header-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }

        .compose-card {
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 1.75rem;
          max-width: 760px;
        }

        .form-group { margin-bottom: 1.25rem; }
        .form-group label { display: block; font-weight: 600; color: #444; margin-bottom: 0.4rem; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.04em; }
        .form-control { width: 100%; padding: 0.65rem 0.8rem; border: 1px solid #ccc; border-radius: 4px; font-size: 0.95rem; box-sizing: border-box; font-family: inherit; }
        .form-control:focus { outline: none; border-color: #007bff; box-shadow: 0 0 0 2px rgba(0,123,255,0.15); }
        textarea.form-control { resize: vertical; }

        /* Segment selector */
        .segment-row { display: flex; border: 1px solid #ccc; border-radius: 6px; overflow: hidden; }
        .segment-btn { flex: 1; padding: 0.6rem 0.5rem; border: none; background: #f5f5f5; cursor: pointer; font-size: 0.85rem; transition: background 0.15s; }
        .segment-btn + .segment-btn { border-left: 1px solid #ccc; }
        .segment-btn:hover { background: #e8e8e8; }
        .segment-btn.active { background: #007bff; color: #fff; font-weight: 700; }

        /* user picker */
        .user-picker-list { border: 1px solid #e0e0e0; border-radius: 4px; max-height: 220px; overflow-y: auto; margin-top: 0.5rem; }
        .picker-item { display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 0.9rem; cursor: pointer; border-bottom: 1px solid #f0f0f0; transition: background 0.1s; }
        .picker-item:last-child { border-bottom: none; }
        .picker-item:hover { background: #f0f7ff; }
        .picker-item.selected { background: #e0efff; }
        .picker-name { font-weight: 600; font-size: 0.9rem; }
        .picker-email { font-size: 0.82rem; color: #777; }
        .picker-empty { text-align: center; color: #999; padding: 1rem; font-size: 0.9rem; }

        .selected-user { background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 4px; padding: 0.5rem 0.8rem; font-size: 0.9rem; display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem; }

        .info-box { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 4px; padding: 0.75rem 1rem; font-size: 0.9rem; color: #1565c0; margin-bottom: 0.5rem; }

        .form-actions { display: flex; gap: 0.75rem; margin-top: 0.5rem; }
        .btn-send { min-width: 140px; font-size: 1rem; padding: 0.7rem 1.5rem; }

        .error-msg   { color: #f44336; font-weight: 600; margin-bottom: 0.75rem; }
        .success-msg { color: #4caf50; font-weight: 600; margin-bottom: 0.75rem; }

        .btn { padding: 0.55rem 1.1rem; border: none; border-radius: 4px; cursor: pointer; font-size: 0.9rem; transition: all 0.2s; }
        .btn-primary { background: #007bff; color: #fff; }
        .btn-primary:hover { background: #0056b3; }
        .btn-secondary { background: #e0e0e0; color: #333; }
        .btn-secondary:hover { background: #c8c8c8; }
        .btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-icon { background: none; border: none; cursor: pointer; color: #999; font-size: 0.9rem; padding: 2px 5px; }
        .btn-icon:hover { color: #f44336; }
      `}</style>
    </div>
  );
}
